import { Form, Modal, Input, Select, Checkbox, InputNumber, DatePicker } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

const { Option } = Select;

const CreateEditConfigModal = ({
  isModalOpen = false,
  supportedValueTypes = {
    STRING: "STRING",
    NUMBER: "NUMBER",
    BOOLEAN: "BOOLEAN",
    ARRAY: "ARRAY",
    DATE: "DATE"
  },
  initialValues = {},
  setIsModalOpen = () => {},
  handleCreateEditConfig = () => {}
}) => {
  const [form] = Form.useForm();
  const [valueType, setValueType] = useState(supportedValueTypes.STRING || "STRING");

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  // When editing, pre-fill the form
  useEffect(() => {
    if (isModalOpen) {
      if (initialValues && Object.keys(initialValues).length > 0) {
        setValueType(initialValues.valueType || supportedValueTypes.STRING);

        const valuesToSet = { ...initialValues };
        if (
          initialValues.valueType === supportedValueTypes.ARRAY &&
          Array.isArray(initialValues.value)
        ) {
          const isSimpleStrings = initialValues.value.every(
            (item) => typeof item === "string" && !item.includes(",")
          );
          if (isSimpleStrings) {
            valuesToSet.value = initialValues.value.join(", ");
          } else {
            valuesToSet.value = JSON.stringify(initialValues.value, null, 2);
          }
        } else if (initialValues.valueType === supportedValueTypes.DATE && initialValues.value) {
          valuesToSet.value = dayjs(initialValues.value);
        }
        form.setFieldsValue(valuesToSet);
      } else {
        form.resetFields();
        setValueType(supportedValueTypes.STRING || "STRING");
      }
    } else {
      // Clear form when modal closes
      form.resetFields();
      setValueType(supportedValueTypes.STRING || "STRING");
    }
  }, [
    isModalOpen,
    initialValues,
    form,
    supportedValueTypes.STRING,
    supportedValueTypes.ARRAY,
    supportedValueTypes.DATE
  ]);

  return (
    <Modal
      title={
        initialValues && Object.keys(initialValues).length > 0
          ? "Edit Configuration"
          : "Create New Configuration"
      }
      open={isModalOpen}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      width={550}
      centered
      okText={initialValues && Object.keys(initialValues).length > 0 ? "Save Changes" : "Create"}
      cancelText="Cancel"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          const payload = { ...values };
          if (
            payload.valueType === supportedValueTypes.ARRAY &&
            typeof payload.value === "string"
          ) {
            const trimmed = payload.value.trim();
            if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
              try {
                payload.value = JSON.parse(trimmed);
              } catch (e) {
                payload.value = trimmed
                  .split(",")
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0);
              }
            } else {
              payload.value = trimmed
                .split(",")
                .map((s) => s.trim())
                .filter((s) => s.length > 0);
            }
          } else if (payload.valueType === supportedValueTypes.DATE && payload.value) {
            payload.value = payload.value.toISOString();
          }
          handleCreateEditConfig(payload);
        }}
        className="mt-4"
      >
        {/* Config Key */}
        <Form.Item
          label="Configuration Key"
          name="key"
          tooltip="A unique identifier for this configuration used in the application"
          rules={[{ required: true, message: "Please enter a config key!" }]}
        >
          <Input
            placeholder="Enter a unique config key"
            disabled={initialValues?._id !== undefined}
          />
        </Form.Item>

        {/* Description */}
        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: "Please enter a description!" }]}
        >
          <Input.TextArea placeholder="Enter a brief description of this config" />
        </Form.Item>

        {/* Value Type Selection */}
        <Form.Item
          label="Value Type"
          name="valueType"
          rules={[{ required: true, message: "Please select a value type!" }]}
        >
          <Select
            onChange={(val) => {
              setValueType(val);
              form.setFieldsValue({ value: undefined });
            }}
            defaultValue={supportedValueTypes.STRING}
            disabled={initialValues?._id !== undefined}
          >
            {Object.values(supportedValueTypes).map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Dynamic Input Field */}
        {valueType === supportedValueTypes.BOOLEAN ? (
          <Form.Item
            label="Value"
            name="value"
            valuePropName="checked"
            rules={[{ required: true, message: "Please check or uncheck!" }]}
          >
            <Checkbox />
          </Form.Item>
        ) : valueType === supportedValueTypes.ARRAY ? (
          <Form.Item
            label="Value"
            name="value"
            rules={[{ required: true, message: "Please enter a value!" }]}
            extra={
              <span style={{ fontSize: "12px", color: "gray" }}>
                <strong>Simple List:</strong> Comma-separated (e.g. <code>apple, banana</code>)
                <br />
                <strong>JSON Array:</strong> For objects (e.g.{" "}
                <code>[&#123;&quot;key&quot;: &quot;val&quot;&#125;]</code>)
              </span>
            }
          >
            <Input.TextArea
              placeholder='apple, banana&#10;OR&#10;[{"regex": "^.*$"}]'
              autoSize={{ minRows: 3, maxRows: 10 }}
            />
          </Form.Item>
        ) : valueType === supportedValueTypes.DATE ? (
          <Form.Item
            label="Value"
            name="value"
            rules={[{ required: true, message: "Please select a date!" }]}
          >
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
        ) : valueType === supportedValueTypes.NUMBER ? (
          <Form.Item
            label="Value"
            name="value"
            rules={[{ required: true, message: "Please enter a number!" }]}
          >
            <InputNumber style={{ width: "100%" }} placeholder="Enter an integer" precision={0} />
          </Form.Item>
        ) : (
          <Form.Item
            label="Value"
            name="value"
            rules={[{ required: true, message: "Please enter a value!" }]}
          >
            <Input placeholder="Enter a string value" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
export default CreateEditConfigModal;
